import {observer} from 'mobx-react-lite';
import React, {useEffect, useState} from 'react';
import {useService} from '../../../../infrastructure/service/service';
import {PageOption} from '../../../../entity/enums/page-option';
import {SplitWithAiDocumentProps} from '../../../../entity/props/title/split-with-ai-document-props';
import Box from '@mui/material/Box';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl, FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {Document} from '../../../../entity/resource/document';
import Skeleton from 'react-loading-skeleton';
import {Auth} from 'aws-amplify';
import {SplitAIDto} from '../../../../entity/dto/split-ai-dto';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {KeyValueResource} from '../../../../entity/resource/key-value-resource';
import {SplitAIRequestDto} from '../../../../entity/dto/split-ai-request-dto';
import {DropdownOption} from '../../../../entity/core/dropdown-option';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/split-document.css';

const SplitWithAIDocument = (props: SplitWithAiDocumentProps): JSX.Element => {
  const {documentService, titleService} = useService();

  const [selectedDocument, setSelectedDocument] = useState<Document>();
  const [documentDetails, setDocumentDetails] = useState<KeyValueResource []>([]);
  const [splitWithAIText, setSplitWithAIText] = useState<string>('');

  const modelOptions = documentService.getModelOptions();

  useEffect(() => {
    const init = async (): Promise<void> => {
      const document = documentService.getDocument();
      setSelectedDocument(document);

      const pageCount = await documentService.getPageCount(document?.url ?? '');
      setDocumentDetails([
        { label: 'Type', value: 'PDF'},
        { label: 'Page Count', value : pageCount.toString() }
      ]);

      setSplitWithAIText('Please Select the AI Model to Split the document.');
    }

    init();
  }, [documentService.getDocument()]);

  const handleViewDocument = (): void => {
    window.open(selectedDocument?.url ?? '', '_blank');
  }

  const handleBackButton = (): void => {
    titleService.setPageOption(PageOption.DOCUMENTS);
  }

  const handleValidate = (values: SplitAIRequestDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.model)
      errors.model = 'AI model is required.';

    return errors;
  }

  const handleSubmit = async (values: SplitAIRequestDto, {setSubmitting}: FormikHelpers<SplitAIRequestDto>): Promise<void> => {
    setSubmitting(true);
    const session = await Auth.currentSession();
    const splitWithAIDto: SplitAIDto = {
      file_uri: selectedDocument?.url ?? '',
      classifier: values.model,
      callback_url: process.env.REACT_APP_AWS_API_ENDPOINT + `/organizations/${props.projectTitle.createdByOrganizationID}/project-titles/${props.projectTitle.id}/documents/split`,
      security_token: session.getIdToken().getJwtToken()
    }

    await documentService.splitWithAI(splitWithAIDto);
    setSplitWithAIText('Your request is still processing. Please refresh Documents section after few minutes.');
    setSubmitting(false);
  }

  return (
    <>
      <Box className={'body-box'}>
        <Card elevation={0} className={'split-document-card'}>
          <CardContent>
            <Box className={'split-document-header'}>
              <KeyboardArrowLeftIcon cursor={'pointer'} onClick={handleBackButton}/>
              <PictureAsPdfIcon className="zitles-icon" />
              <Typography onClick={handleViewDocument} className={'split-document-name'}>{selectedDocument?.name ?? ''}</Typography>
            </Box>
            <Box className={'split-document-body'}>
              {
                documentDetails.map((data: KeyValueResource, index: number): JSX.Element =>
                  <Box key={index} className={'template-item split-document-details'}>
                    <Typography className={'template-data-label user'}>{data.label}</Typography>
                    <Typography className={'template-data-value'}>{data.value ?? <Skeleton/>}</Typography>
                  </Box>
                )
              }
              <Formik
                initialValues={titleService.getSplitAIRequestDto()}
                validate={handleValidate}
                onSubmit={handleSubmit}>
                {({values, handleChange, handleSubmit, handleBlur, touched, errors, isSubmitting}: FormikProps<SplitAIRequestDto>): JSX.Element => (
                  <Form onSubmit={handleSubmit}>
                    <Box className={'split-with-ai-container'}>
                      <Typography className={'split-with-ai-text'}>{splitWithAIText}</Typography>
                      <FormControl className={'split-with-ai-input'} required error={touched.model && Boolean(errors.model)}>
                        <InputLabel>AI Model</InputLabel>
                        <Select
                          value={values.model}
                          name={'model'}
                          label='AI Model'
                          onBlur={handleBlur}
                          onChange={handleChange}>
                          {
                            modelOptions.map((option: DropdownOption): JSX.Element => <MenuItem key={option.key} value={option.value}>{option.text}</MenuItem>)
                          }
                        </Select>
                        <FormHelperText>{touched.model && errors.model}</FormHelperText>
                      </FormControl>
                      <Button
                        type={'submit'}
                        disableElevation
                        className={`zitles-button split-with-ai-button submit ${isSubmitting ? 'disable' : ''}`}
                        variant='contained'
                        startIcon={isSubmitting ? <CircularProgress size={14} color='inherit' /> : null}>
                        {
                          isSubmitting ? 'Splitting' : 'Split with AI'
                        }
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}

export default observer(SplitWithAIDocument);
